import React, { useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { Props, State, EmployeeGroupQuickFilter } from "./interfaces";
import { EmployeeGroupsApiService } from "../../../services/employee-groups/employee-groups-api.service";

import { EmployeeGroupDTO } from "../../../models/employee-groups";

import useAbortableLoadResponse from "../../../custom-hooks/useAbortableLoadResponse/useAbortableLoadResponse";

import QuickFilter from "../../../reusable-components/QuickFilter/QuickFilter";
import { isAuthorizedEeGroup } from "src/custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import { useAppSelector } from "src/custom-hooks/redux/redux";

export const EmployeeGroupQuickfilter = <Q extends EmployeeGroupQuickFilter>({
  quickFilters,
  handleChangeQuickFilters,
  allEmployeeGroupsOption,
  isMultiSelect,
  authorizations,
}: Props<Q>) => {
  const { t } = useTranslation();
  const tRP = "components.quickfilters.employee_groups";
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const [options, setOptions] = useState<State>({
    customOptions: [
      ...(allEmployeeGroupsOption
        ? [
            {
              name: t(`${tRP}.all_groups`),
              value: { employeeGroupIds: [] },
            },
          ]
        : []),
    ],
    computedOptions: [],
  });

  const { response } = useAbortableLoadResponse<EmployeeGroupDTO>(EmployeeGroupsApiService, {
    sorts: undefined, // so that service API default sort is applied
    page: { pageNumber: 0, pageSize: 100 },
  });

  useEffect(() => {
    if (!!response?.items.length) {
      const authorizedEmployeeGroups = authorizations
        ? response.items.filter((employeeGroup) =>
            isAuthorizedEeGroup([...authorizations], currentUserAuthorizations, [employeeGroup.id])
          )
        : response.items;

      const newOptions = authorizedEmployeeGroups.map(({ name, id }) => ({
        name,
        value: { employeeGroupIds: [id] },
      }));

      setOptions((state) => ({ ...state, computedOptions: newOptions }));
    }
  }, [response?.items, authorizations]);

  return (
    <QuickFilter
      options={[...options.customOptions, ...options.computedOptions]}
      quickFilters={quickFilters}
      quickFilter="employeeGroups"
      handleChangeQuickFilter={handleChangeQuickFilters}
      defaultLabel={t(`${tRP}.default_label`)}
      isMultiSelect={isMultiSelect}
      shrinkedLabel={t(`${tRP}.shrinked_label`)}
      multiSelectField="employeeGroupIds"
    />
  );
};

export default memo(EmployeeGroupQuickfilter);
